"use client";

import { useAuth } from "@clerk/nextjs";
import { DatadogProvider } from "@datadog/openfeature-browser";
import { OpenFeature } from "@openfeature/web-sdk";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { buildFeatureFlagContext, featureFlagContextKey } from "@/lib/feature-flags/context";
import { FeatureFlagsClientContext, type FeatureFlagsClientState } from "@/lib/feature-flags/client";

let providerReady: Promise<void> | null = null;

function readEnv(value: string | undefined) {
  return typeof value === "string" && value.trim().length > 0 ? value.trim() : null;
}

function datadogBrowserConfig() {
  const clientToken = readEnv(process.env.NEXT_PUBLIC_DD_FEATURE_FLAGS_CLIENT_TOKEN);
  if (!clientToken) return null;

  return {
    clientToken,
    applicationId: readEnv(process.env.NEXT_PUBLIC_DD_FEATURE_FLAGS_APPLICATION_ID)
      || readEnv(process.env.NEXT_PUBLIC_DD_RUM_APPLICATION_ID)
      || undefined,
    site: readEnv(process.env.NEXT_PUBLIC_DD_FEATURE_FLAGS_SITE)
      || readEnv(process.env.NEXT_PUBLIC_DD_RUM_SITE)
      || readEnv(process.env.NEXT_PUBLIC_DD_SITE)
      || "datadoghq.com",
    env: readEnv(process.env.NEXT_PUBLIC_DD_FEATURE_FLAGS_ENV)
      || readEnv(process.env.NEXT_PUBLIC_DD_RUM_ENV)
      || readEnv(process.env.NEXT_PUBLIC_DD_ENV)
      || process.env.NODE_ENV,
    service: readEnv(process.env.NEXT_PUBLIC_DD_FEATURE_FLAGS_SERVICE)
      || readEnv(process.env.NEXT_PUBLIC_DD_RUM_SERVICE)
      || "scout",
    version: readEnv(process.env.NEXT_PUBLIC_DD_VERSION) || undefined,
    enableExposureLogging: true,
    enableFlagEvaluationTracking: true,
    enableRumFeatureFlagTracking: true,
  };
}

async function configureDatadogProvider(context: ReturnType<typeof buildFeatureFlagContext>) {
  const config = datadogBrowserConfig();
  if (!config) return false;

  if (!providerReady) {
    providerReady = OpenFeature.setProviderAndWait(new DatadogProvider(config), context);
    await providerReady;
    return true;
  }

  await providerReady;
  await OpenFeature.setContext(context);
  return true;
}

export function FeatureFlagsProvider({ children }: { children: ReactNode }) {
  const { isLoaded, userId, orgId, orgRole } = useAuth();
  const [state, setState] = useState<FeatureFlagsClientState>({
    configured: Boolean(datadogBrowserConfig()),
    ready: false,
    contextKey: "loading",
  });
  const context = useMemo(() => buildFeatureFlagContext({ userId, orgId, orgRole }), [orgId, orgRole, userId]);
  const contextKey = useMemo(() => featureFlagContextKey(context), [context]);

  useEffect(() => {
    if (!isLoaded) return;

    let cancelled = false;
    void configureDatadogProvider(context)
      .then((configured) => {
        if (!cancelled) setState({ configured, ready: configured, contextKey });
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setState({
            configured: true,
            ready: false,
            contextKey,
            error: error instanceof Error ? error.message : "Feature flag provider failed to initialize",
          });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [context, contextKey, isLoaded]);

  return (
    <FeatureFlagsClientContext.Provider value={state}>
      {children}
    </FeatureFlagsClientContext.Provider>
  );
}
